import {getOptionsFromValues} from "../../../../../utils/General";
import {IManifestCardConfigurationsV2} from "../types/manifest-card-configurations.type";
import {
  CardTypeV2,
  DriverInformationValue,
  HoverOnDriversValues,
  HoverOnStopsValues,
  StopInformationValue,
  TopStopInformationValue
} from "./enums";

export const SEPARATOR_WIDTH = 4; // Width of a separator in pixels

export const AVAILABLE_WIDTH_MAP: Record<CardTypeV2, number> = {
  [CardTypeV2.Detailed]: 224,
  [CardTypeV2.Compact]: 160,
  [CardTypeV2.Minimal]: 160,
  [CardTypeV2.Micro]: 0
};

export const FIXED_WIDTH_FIELDS_MAP: Map<string, number> = new Map([[StopInformationValue.ServiceType, 42]]);

export const DISABLED_FIELDS_MESSAGE_MAP: Map<string, string> = new Map([
  [StopInformationValue.ServiceType, "The Service Type data point has a set required width."]
]);

export const HOVERS_ON_DRIVERS_OPTIONS = getOptionsFromValues(Object.values(HoverOnDriversValues));

/**
 * DP-3156: display labels for the stop hover options, where the label differs from the
 * enum value. The value is what gets persisted in the user's manifestCardConfigurations
 * preference and what drives behaviour (HOVER_STOPS_FIELD_MAP, the ManifestCardCommonV2
 * switch), so renaming it would orphan every saved selection. Only `key` — the label and
 * the dropdown search text — changes.
 *
 * NOTE: HoverOnDriversValues has its own ALIAS = "Alias" meaning the *driver* alias. It is
 * built above from the plain helper and must not pick up this label.
 */
export const HOVER_ON_STOPS_LABELS: Partial<Record<HoverOnStopsValues, string>> = {
  [HoverOnStopsValues.ALIAS]: "Order Alias"
};

export const HOVERS_ON_STOPS_OPTIONS = Object.values(HoverOnStopsValues).map((value) => ({
  key: HOVER_ON_STOPS_LABELS[value] ?? value,
  value
}));

const DEFAULT_DRIVER_HOVER_CONFIG = HOVERS_ON_DRIVERS_OPTIONS.filter(
  (option) => option.value === HoverOnDriversValues.MANIFEST_ID
);
const DEFAULT_STOPS_HOVER_CONFIG = HOVERS_ON_STOPS_OPTIONS.filter((option) =>
  [HoverOnStopsValues.CUSTOMER, HoverOnStopsValues.STOP_PHONE].includes(option.value as HoverOnStopsValues)
);

export const DEFAULT_CARD_CONFIGURATIONS_VALUE: IManifestCardConfigurationsV2 = {
  cardType: CardTypeV2.Compact,
  driverInformation: [DriverInformationValue.DriverCodeAndName, DriverInformationValue.DriverCapabilities],
  stopInformation: [
    {
      field: StopInformationValue.AddressAndZone,
      width:
        AVAILABLE_WIDTH_MAP[CardTypeV2.Compact] -
        SEPARATOR_WIDTH -
        FIXED_WIDTH_FIELDS_MAP.get(StopInformationValue.ServiceType)!,
      isDisabled: true,
      hoverContent: ""
    },
    {
      field: StopInformationValue.ServiceType,
      width: FIXED_WIDTH_FIELDS_MAP.get(StopInformationValue.ServiceType)!,
      isDisabled: true,
      hoverContent: DISABLED_FIELDS_MESSAGE_MAP.get(StopInformationValue.ServiceType)
    }
  ],
  topStopInformation: TopStopInformationValue.LastCompletedStop,
  hoverDriverConfig: DEFAULT_DRIVER_HOVER_CONFIG,
  hoverStopsConfig: DEFAULT_STOPS_HOVER_CONFIG
};

// Default stop information for tenants using the ETA service (DP-3052).
// ETA (primary) + Address + Zone; widths sum to getAvailableWidth(Compact, 1) = 156.
export const DEFAULT_STOP_INFORMATION_WITH_ETA: IManifestCardConfigurationsV2["stopInformation"] = [
  {field: StopInformationValue.ETATime, width: 36, isDisabled: false, hoverContent: ""},
  {field: StopInformationValue.AddressAndZone, width: 120, isDisabled: false, hoverContent: ""}
];

// Default manifest card config, gated on the ETA service: tenants with the ETA service get
// the ETA default; otherwise they keep DEFAULT_CARD_CONFIGURATIONS_VALUE (Address + Zone + Service Type).
export const getDefaultCardConfigurations = (isUseETAService: boolean | undefined): IManifestCardConfigurationsV2 =>
  isUseETAService
    ? {...DEFAULT_CARD_CONFIGURATIONS_VALUE, stopInformation: DEFAULT_STOP_INFORMATION_WITH_ETA}
    : DEFAULT_CARD_CONFIGURATIONS_VALUE;
